import { useState, useEffect, useCallback, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import FormInput from "../../../components/Form/FormInput";
import FormTextarea from "../../../components/Form/TextArea";
import { toast, confirmDialog } from "../../../components/Toast/toast";
import { btn } from "../../../components/Button/buttonStyles";
import { AuthContext } from "../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../utils/constants/permissions";

const RESOURCES = {
  "client-invoice": {
    endpoint: (id) => `/project-finances/client-invoices/${id}/write-off`,
    invoicePath: (id) => `/client/invoice/${id}`,
    partyLabel: "Client",
    partyPath: (id) => `/clients/${id}`,
    noteLabel: "Note for client",
    module: MODULES.CLIENT_INVOICES,
  },
  "vendor-invoice": {
    endpoint: (id) => `/project-finances/vendor-invoices/${id}/write-off`,
    invoicePath: (id) => `/vendor/invoice/${id}`,
    partyLabel: "Vendor",
    partyPath: (id) => `/vendors/${id}`,
    noteLabel: "Note for vendor",
    module: MODULES.VENDOR_INVOICES,
  },
};

const fmtNum = (v) => Number(v || 0).toFixed(2);

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const Field = ({ label, children }) => (
  <div className="flex">
    <span className="w-52 font-medium text-gray-700 shrink-0">{label}:</span>
    <span className="text-gray-900 whitespace-pre-line">{children ?? "—"}</span>
  </div>
);

const WriteOffPage = ({ resource }) => {
  const config = RESOURCES[resource];
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const readOnly = isReadOnly(user, config.module);

  const [data, setData] = useState(null);
  const [form, setForm] = useState({ amount: "", note: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(() => new Date());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(config.endpoint(id), { withCredentials: true });
      setData(res.data.data);
      setForm({ amount: "", note: "" });
    } catch (err) {
      console.error(err);
      setLoadError(err.response?.data?.message || "Failed to load the invoice.");
    } finally {
      setLoading(false);
    }
  }, [config, id]);

  useEffect(() => {
    if (id) fetchData();
  }, [id, fetchData]);

  // "Write off at" is the moment of submitting; the server stamps the real time.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  if (loading)
    return (
      <div className="flex justify-center items-center mt-16">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  if (loadError)
    return <div className="text-center mt-10 text-danger-600 font-medium">{loadError}</div>;
  if (!data) return null;

  const currency = data.currency || "";
  const canWriteOff = !readOnly && !data.blocked;
  const history = data.write_offs || [];

  const setField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "amount") setError("");
  };

  const handleSubmit = async () => {
    const amount = Number(form.amount);
    if (form.amount === "" || !Number.isFinite(amount) || amount <= 0)
      return setError("Enter an amount greater than 0.");
    if (amount > data.max_amount)
      return setError(`The write-off cannot be more than the ${fmtNum(data.max_amount)} still due.`);
    const question =
      `Write off ${fmtNum(amount)} ${currency} on invoice ${data.code}? ` +
      `The due amount goes from ${fmtNum(data.due_amount)} to ${fmtNum(data.due_amount - amount)}. ` +
      "A posted write-off cannot be changed.";
    if (!(await confirmDialog(question))) return;

    setSaving(true);
    try {
      await api.post(config.endpoint(id), { amount, note: form.note }, { withCredentials: true });
      toast.success(`Write-off added to invoice ${data.code}.`);
      setError("");
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to write off the invoice.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <BackButton onClick={() => navigate(config.invoicePath(id))} />
        <h2 className="text-[22px] font-semibold tracking-tight text-gray-900">
          Write off: Invoice {data.code}
        </h2>
      </div>

      {data.blocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          {data.blocked}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-8 space-y-4 text-sm">
        <Field label={config.partyLabel}>
          {data.party_id ? (
            <Link to={config.partyPath(data.party_id)} className="text-brand-600 hover:text-brand-800 underline">
              {data.party_name || "—"}
            </Link>
          ) : (
            data.party_name
          )}
        </Field>
        <Field label="Issued at">{fmtDateTime(data.issued_at)}</Field>
        <Field label="Currency">{data.currency}</Field>
        <Field label="Total">{fmtNum(data.total)}</Field>
        <Field label="Paid amount">{fmtNum(data.paid_amount)}</Field>
        <Field label="Written off so far">{fmtNum(data.write_off_amount)}</Field>
        <Field label="Due amount">{`${fmtNum(data.due_amount)} ${currency}`}</Field>
        <Field label={config.noteLabel}>{data.note}</Field>
        <Field label="Internal note">{data.internal_note}</Field>
      </div>

      <div className="w-full bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b text-sm font-semibold text-gray-900">Write-off history</div>
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                {["#", "Written off at", "Due before", "Write off amount", "Due after", "Note", "Written off by"].map(
                  (h) => (
                    <th key={h} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400 text-xs">
                    This invoice has not been written off.
                  </td>
                </tr>
              ) : (
                history.map((w, i) => (
                  <tr key={w.id} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-3 py-2 text-xs text-center">{i + 1}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">{fmtDateTime(w.at)}</td>
                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">{fmtNum(w.due_before)}</td>
                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap font-semibold">
                      {fmtNum(w.amount)}
                    </td>
                    <td className="px-3 py-2 text-xs text-right whitespace-nowrap">{fmtNum(w.due_after)}</td>
                    <td className="px-3 py-2 text-xs text-gray-700 whitespace-pre-line">{w.note || "—"}</td>
                    <td className="px-3 py-2 text-xs text-center whitespace-nowrap">{w.by || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
            {history.length > 0 && (
              <tfoot>
                <tr className="border-t text-xs font-semibold text-gray-900">
                  <td colSpan={3} className="px-3 py-2 text-right">
                    Total written off
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtNum(data.write_off_amount)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtNum(data.due_amount)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {canWriteOff && (
        <div className="bg-white shadow rounded-lg p-8">
          <div className="space-y-5 max-w-xl">
            <h3 className="text-sm font-semibold text-gray-900">Add a write-off</h3>
            <FormInput
              label={`Write off amount (up to ${fmtNum(data.max_amount)})`}
              name="amount"
              type="number"
              value={form.amount}
              onChange={setField}
              error={error}
              required
              disabled={saving}
              suffix={currency || undefined}
            />
            <FormInput label="Write off at" name="written_off_at" value={fmtDateTime(now)} disabled />
            <FormTextarea
              label="Write off note"
              name="note"
              value={form.note}
              onChange={setField}
              disabled={saving}
            />
            <button onClick={handleSubmit} disabled={saving} className={btn("danger", "sm")}>
              Write off
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WriteOffPage;
